import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { cartAPI } from '../lib/api';
import { useAuth } from './AuthContext';
import { trackAddToCart } from '../utils/tracking';

const CartContext = createContext(null);

const GUEST_CART_KEY = 'amidex_guest_cart';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, discount: 0, total: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true); // Start as true to prevent redirects before first fetch
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const prevAuthRef = useRef(isAuthenticated);

  // Load guest cart from localStorage
  const loadGuestCart = useCallback(() => {
    try {
      const saved = localStorage.getItem(GUEST_CART_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load guest cart:', e);
    }
    return { items: [], subtotal: 0, discount: 0, total: 0 };
  }, []);

  // Save guest cart to localStorage
  const saveGuestCart = useCallback((cartData) => {
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartData));
    } catch (e) {
      console.error('Failed to save guest cart:', e);
    }
  }, []);

  // Calculate cart totals for guest cart
  const calculateGuestCartTotals = useCallback((items, products) => {
    const subtotal = items.reduce((sum, item) => {
      const product = products.find(p => (p._id || p.id) === item.product_id);
      return sum + (product?.price || 0) * item.quantity;
    }, 0);
    return {
      items,
      subtotal,
      discount: 0,
      total: subtotal,
    };
  }, []);

  // Merge guest cart with server cart
  const mergeGuestCartWithServer = useCallback(async () => {
    const guestCart = loadGuestCart();
    if (!guestCart.items || guestCart.items.length === 0) {
      return null;
    }

    try {
      // Convert guest cart items to the format expected by the API
      const itemsToMerge = guestCart.items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity
      }));

      // Call the merge API
      const response = await cartAPI.merge(itemsToMerge);
      
      // Clear the guest cart after successful merge
      localStorage.removeItem(GUEST_CART_KEY);
      
      return response.data;
    } catch (error) {
      console.error('Failed to merge guest cart:', error);
      // Still clear the guest cart to avoid duplicate merge attempts
      localStorage.removeItem(GUEST_CART_KEY);
      return null;
    }
  }, [loadGuestCart]);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      // Load guest cart from localStorage
      const guestCart = loadGuestCart();
      setCart(guestCart);
      setLoading(false);
      setInitialFetchDone(true);
      return;
    }
    
    try {
      setLoading(true);
      const response = await cartAPI.get();
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
      setInitialFetchDone(true);
    }
  }, [isAuthenticated, loadGuestCart]);

  // Handle authentication state changes - merge cart when user logs in
  useEffect(() => {
    const handleAuthChange = async () => {
      // Detect login: was not authenticated, now is authenticated
      if (!prevAuthRef.current && isAuthenticated) {
        setLoading(true);
        try {
          // First, merge the guest cart with the server cart
          const mergedCart = await mergeGuestCartWithServer();
          if (mergedCart) {
            setCart(mergedCart);
          } else {
            // If no merge needed or merge failed, just fetch the server cart
            const response = await cartAPI.get();
            setCart(response.data);
          }
        } catch (error) {
          console.error('Failed to handle login cart merge:', error);
        } finally {
          setLoading(false);
        }
      } else if (prevAuthRef.current && !isAuthenticated) {
        // User logged out - load guest cart
        const guestCart = loadGuestCart();
        setCart(guestCart);
      } else if (!isAuthenticated) {
        // Initial load when not authenticated
        const guestCart = loadGuestCart();
        setCart(guestCart);
      } else {
        // Initial load when authenticated
        fetchCart();
      }
      
      // Update the ref for next comparison
      prevAuthRef.current = isAuthenticated;
    };

    handleAuthChange();
  }, [isAuthenticated, mergeGuestCartWithServer, loadGuestCart, fetchCart]);

  // Initial cart fetch on mount
  useEffect(() => {
    fetchCart();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addToCart = async (productId, quantity = 1, productData = null, region = 'US') => {
    // Track AddToCart event
    if (productData) {
      trackAddToCart({
        id: productId,
        slug: productId,
        name: productData.name || '',
        price: productData.price || 0,
        currency: productData.currency || 'USD'
      }, quantity, region);
    }
    
    if (!isAuthenticated) {
      // Guest cart - store locally
      const guestCart = loadGuestCart();
      const existingIndex = guestCart.items.findIndex(item => item.product_id === productId);
      
      if (existingIndex >= 0) {
        guestCart.items[existingIndex].quantity += quantity;
      } else {
        guestCart.items.push({
          product_id: productId,
          quantity,
          product: productData, // Store product data for display
        });
      }
      
      // Recalculate totals
      const subtotal = guestCart.items.reduce((sum, item) => {
        const price = item.product?.price || 0;
        return sum + price * item.quantity;
      }, 0);
      
      guestCart.subtotal = subtotal;
      guestCart.total = subtotal;
      
      saveGuestCart(guestCart);
      setCart(guestCart);
      setIsOpen(true);
      return guestCart;
    }
    
    try {
      setLoading(true);
      const response = await cartAPI.add(productId, quantity);
      setCart(response.data);
      setIsOpen(true);
      return response.data;
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!isAuthenticated) {
      // Guest cart
      const guestCart = loadGuestCart();
      const item = guestCart.items.find(i => i.product_id === productId);
      if (item) {
        item.quantity = quantity;
      }
      
      const subtotal = guestCart.items.reduce((sum, item) => {
        const price = item.product?.price || 0;
        return sum + price * item.quantity;
      }, 0);
      
      guestCart.subtotal = subtotal;
      guestCart.total = subtotal;
      
      saveGuestCart(guestCart);
      setCart(guestCart);
      return guestCart;
    }
    
    try {
      setLoading(true);
      const response = await cartAPI.update(productId, quantity);
      setCart(response.data);
      return response.data;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    if (!isAuthenticated) {
      // Guest cart
      const guestCart = loadGuestCart();
      guestCart.items = guestCart.items.filter(i => i.product_id !== productId);
      
      const subtotal = guestCart.items.reduce((sum, item) => {
        const price = item.product?.price || 0;
        return sum + price * item.quantity;
      }, 0);
      
      guestCart.subtotal = subtotal;
      guestCart.total = subtotal;
      
      saveGuestCart(guestCart);
      setCart(guestCart);
      return guestCart;
    }
    
    try {
      setLoading(true);
      const response = await cartAPI.remove(productId);
      setCart(response.data);
      return response.data;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated) {
      localStorage.removeItem(GUEST_CART_KEY);
      setCart({ items: [], subtotal: 0, discount: 0, total: 0 });
      return;
    }
    
    try {
      setLoading(true);
      await cartAPI.clear();
      setCart({ items: [], subtotal: 0, discount: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);

  const itemCount = cart.items.reduce((acc, item) => acc + item.quantity, 0);

  const value = {
    cart,
    loading,
    initialFetchDone,
    isOpen,
    itemCount,
    isAuthenticated,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    openCart,
    closeCart,
    fetchCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
